import * as currency from 'currency-formatter';
import { useRef } from 'react';
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { Field, MoneyPrefix } from '@/components/ui/admin/field';
import { parseCurrency } from '@/lib/currency';
import { cn } from '@/lib/utils';
import { type ColSpan, spanToClassName } from './form-input';

type FormMaskedInputProps<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  placeholder?: string;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  autoComplete?: string;
  sm?: ColSpan;
  md?: ColSpan;
  lg?: ColSpan;
  className?: string;
} & React.ComponentProps<'input'>;

export function FormCurrencyInput<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  autoComplete,
  sm,
  md,
  lg,
  className,
  required,
  ...props
}: FormMaskedInputProps<T>) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const moveCursor = (el: HTMLInputElement, digitIndex: number) => {
    const formatted = el.value;

    let digitCount = 0;
    let pos = formatted.length;

    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        if (digitCount === digitIndex) {
          pos = i;
          break;
        }
        digitCount++;
      }
    }

    el.setSelectionRange(pos, pos + 1);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div className={cn(spanToClassName(sm, md, lg), className)}>
          <Field
            {...props}
            id={name}
            label={label}
            required={required}
            invalid={fieldState.invalid}
            displayValue
            leading={<MoneyPrefix />}
            ref={(el) => {
              inputRef.current = el;
              field.ref(el);
            }}
            value={
              field.value
                ? currency.format(field.value, { code: 'BRL', symbol: '' })
                : '0,00'
            }
            type="text"
            autoComplete={autoComplete}
            placeholder={placeholder}
            aria-invalid={fieldState.invalid}
            onClick={() => {
              const el = inputRef.current;
              if (!el) return;

              const pos = el.selectionStart ?? 0;
              el.setSelectionRange(pos, pos + 1);
            }}
            onKeyDown={(e) => {
              const el = inputRef.current;
              if (!el) return;

              const raw = el.value;
              const digits = raw.replace(/\D/g, '').split('');

              const start = el.selectionStart ?? 0;

              let digitIndex = raw.slice(0, start).replace(/\D/g, '').length;

              if (e.key === 'ArrowRight') {
                e.preventDefault();

                digitIndex++;

                moveCursor(el, digitIndex);
                return;
              }

              if (e.key === 'ArrowLeft') {
                e.preventDefault();

                digitIndex = Math.max(0, digitIndex - 1);

                moveCursor(el, digitIndex);
                return;
              }

              if (/^\d$/.test(e.key)) {
                e.preventDefault();

                digits[digitIndex] = e.key;

                const newNumber = Number(digits.join('')) / 100;
                field.onChange(newNumber);

                requestAnimationFrame(() => {
                  moveCursor(el, digitIndex + 1);
                });

                return;
              }
            }}
            onChange={(e) => {
              const parsed = parseCurrency(e.target.value);
              field.onChange(parsed);
            }}
          />

          {fieldState.error && (
            <p className="mt-1.5 text-xs font-medium text-destructive">
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  );
}
